import { useState, useEffect, type ReactElement } from 'react';

export interface AdminConfirmationPopupProps {
  innerHTML?: ReactElement;
  onConfirm: () => void;
  onCancel: () => void;
  /** Optional file to preview inside the popup */
  file?: File | null;
  projectName?: string;
  onProjectNameChange?: (v: string) => void;
}

export function AdminConfirmationPopup({
  innerHTML,
  onConfirm,
  onCancel,
  file = null,
}: AdminConfirmationPopupProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // create object URL for file previews (images)
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    try {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (e) {
      setPreviewUrl(null);
    }
  }, [file]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let size = bytes / 1024;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  return (
    <div className="fixed h-full w-full inset-0 flex items-center justify-center z-50">
      <div className="absolute top-0 left-0 w-full h-full bg-black opacity-50 z-5"></div>
      <div className="bg-light-bg p-6 rounded-lg shadow-lg z-10 w-full max-w-2xl mx-4">
        <h2 className="text-xl font-bold mb-4">Confirm Action</h2>

        {file ? (
          <div className="mb-4 flex flex-col md:flex-row gap-4 items-start">
            <div className="w-full md:w-40 h-40 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden border border-light-border">
              {previewUrl && file.type.startsWith('image/') ? (
                <img src={previewUrl} alt={file.name} className="object-contain w-full h-full" />
              ) : (
                <div className="text-center px-3">
                  <div className="text-2xl font-semibold mb-1">{(file.name.split('.').pop() || '').toUpperCase()}</div>
                  <div className="text-sm text-gray-600">{file.type || 'Unknown'}</div>
                </div>
              )}
            </div>
            <div className="flex-1 text-sm text-gray-700">
              <p className="font-medium">{file.name}</p>
              <p className="mt-1">Size: {formatSize(file.size)}</p>
              <p className="mt-1">Type: {file.type || 'Unknown'}</p>
            </div>
          </div>
        ) : (
          <div className="mb-4">{innerHTML}</div>
        )}

        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-light-bg border border-light-border text-light-text rounded-md hover:bg-gray-100 cursor-pointer"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-light-main text-white rounded hover:bg-light-main-hover cursor-pointer"
            onClick={onConfirm}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
